import { User, ChevronDown, Check, PlusCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";

interface CustomerOption {
  id: number;
  name: string;
}

interface InlineCustomerSelectProps {
  customerName: string | undefined;
  customerId: number;
  customers: CustomerOption[] | undefined;
  open: boolean;
  search: string;
  onOpenChange: (open: boolean) => void;
  onSearchChange: (value: string) => void;
  onSelect: (customerId: number) => void;
  onCreateNew: (name: string) => void;
}

export function InlineCustomerSelect({
  customerName,
  customerId,
  customers,
  open,
  search,
  onOpenChange,
  onSearchChange,
  onSelect,
  onCreateNew,
}: InlineCustomerSelectProps) {
  const trimmed = search.trim();
  const filtered = (customers ?? []).filter(c =>
    !trimmed || c.name.toLowerCase().includes(trimmed.toLowerCase())
  );
  const hasExactMatch = (customers ?? []).some(c => c.name.toLowerCase() === trimmed.toLowerCase());
  const showCreate = trimmed.length > 0 && !hasExactMatch;

  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <User className="w-4 h-4 text-muted-foreground shrink-0" />
      <Popover
        open={open}
        onOpenChange={(next) => {
          onOpenChange(next);
          if (!next) onSearchChange("");
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className="hover:text-primary font-medium flex items-center gap-1 group text-muted-foreground"
            title="Change customer"
          >
            {customerName ?? "No customer"}
            <ChevronDown className="w-3 h-3 opacity-0 group-hover:opacity-70 transition-opacity" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command>
            <CommandInput
              placeholder="Search customer..."
              value={search}
              onValueChange={onSearchChange}
            />
            <CommandList>
              {filtered.length === 0 && !showCreate && (
                <CommandEmpty>No customers found.</CommandEmpty>
              )}
              {filtered.length > 0 && (
                <CommandGroup>
                  {filtered.map(c => (
                    <CommandItem
                      key={c.id}
                      value={c.name}
                      onSelect={() => onSelect(c.id)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          c.id === customerId ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {c.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {showCreate && (
                <CommandGroup>
                  <CommandItem
                    value={`__create__${trimmed}`}
                    onSelect={() => onCreateNew(trimmed)}
                  >
                    <PlusCircle className="mr-2 h-4 w-4 text-muted-foreground" />
                    Create &quot;{trimmed}&quot;
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </span>
  );
}
